import React from 'react';
import { useId } from '@reach/auto-id';

import { useField, useFieldDescription } from '../../context/form.context';
import { CheckboxGroup } from '../checkbox-group';
import { ErrorMessage } from '../error-message.component';
import { FieldLabel } from './field-label.component';

export type CheckboxGroupFieldProps = {
  name: string;
  label?: string;
  showRequiredIndicator?: boolean;
  className?: string;
} & Omit<
  React.ComponentProps<typeof CheckboxGroup>,
  'value' | 'onChange' | 'hasError'
>;

export const CheckboxGroupField = ({
  name,
  label: labelProp,
  className,
  showRequiredIndicator = true,
  ...rest
}: CheckboxGroupFieldProps) => {
  const [field, meta, helpers] = useField(name);
  const { fieldLabel, required } = useFieldDescription(name);
  const label = labelProp ?? fieldLabel;
  const { error, touched } = meta;
  const hasError = Boolean(error && touched);
  const labelId = useId()!;
  const errorId = useId()!;

  return (
    <div
      className={className}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={hasError ? errorId : undefined}
    >
      <FieldLabel
        id={labelId}
        hasError={hasError}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        {label}
      </FieldLabel>

      <CheckboxGroup
        hasError={hasError}
        className="ml-1"
        {...field}
        {...rest}
        onChange={(value) => {
          helpers.setValue(value);
        }}
      />
      {hasError && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};
